/**
 * MODULE HOME — level 1 of the grammar (09 §1.4). `/c/{company_uid}/m/{module}`.
 *
 * It lists this module's PARENT SCREENS. Nothing here is a toolbar button whose label is a noun,
 * because 09 §1.3 is binding and it is the rule that gets violated: a noun is a screen, a verb is
 * an action. "ניירות עבודה" is a screen. "נעילת שנה" is an action.
 *
 * Only `company_management` and `billing` are `active` in the module catalogue. Every other module
 * is `planned` and renders §5.8's written state rather than an invented screen.
 */

import { useEffect } from 'react';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { StateBlock } from '../components/StateBlock';
import { DenialNotice } from '../components/DenialNotice';
import { routes } from '../routes';

export interface ModuleHomeScreenProps {
  readonly companyUid: string;
  readonly moduleCode: string;
}

/**
 * The parent screens each ACTIVE module offers. Deliberately short: this is the whole of what
 * exists today. Nothing is listed here that does not have a module code behind it.
 */
const PARENT_SCREENS: Readonly<Record<string, readonly { slug: string; titleHe: string }[]>> = {
  company_management: [
    { slug: 'companies', titleHe: 'חברות במשרד' },
    { slug: 'sources', titleHe: 'מקורות נתונים' },
  ],
  billing: [{ slug: 'subscription', titleHe: 'המנוי של המשרד' }],
};

export function ModuleHomeScreen({ companyUid, moduleCode }: ModuleHomeScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const { setCompanyByUid, setModuleByCode, setEntityLabel } = useDeclareScreenContext();

  useEffect(() => {
    setCompanyByUid(companyUid);
    setModuleByCode(moduleCode);
    setEntityLabel(null);
  }, [companyUid, moduleCode, setCompanyByUid, setModuleByCode, setEntityLabel]);

  const back = { labelHe: 'דף הבית של החברה', href: routes.companyHome(companyUid) };
  const entitlement = ctx.modules.find((m) => m.moduleCode === moduleCode) ?? null;

  if (entitlement === null) {
    return (
      <AppShell titleHe="המודול הזה אינו קיים" backToParent={back}>
        <StateBlock
          titleHe="הדף שחיפשת לא קיים"
          bodyHe="החלק שביקשת אינו קיים במערכת."
          nextStep={<a className="btn" href={back.href}>חזרה ל{back.labelHe}</a>}
        />
      </AppShell>
    );
  }

  if (!entitlement.available) {
    return (
      <AppShell titleHe={entitlement.displayNameHe} backToParent={back}>
        {/*
          The server decided. This renders its decision — it does not re-derive one.
          O-3: no pricing and no upgrade button in a "not subscribed" message.
        */}
        <DenialNotice
          deny={{
            error: 'not_permitted',
            ...(entitlement.unavailableReason ? { reason: entitlement.unavailableReason } : {}),
            requestUid: '',
          }}
        />
      </AppShell>
    );
  }

  if (entitlement.catalogueStatus !== 'active') {
    return (
      <AppShell titleHe={entitlement.displayNameHe} backToParent={back}>
        <StateBlock
          titleHe="לא זמין"
          bodyHe="החלק הזה טרם נפתח במערכת. כשהוא ייפתח הוא יופיע כאן."
          tone="unavailable"
          nextStep={<a className="btn" href={back.href}>חזרה ל{back.labelHe}</a>}
        />
      </AppShell>
    );
  }

  const parents = PARENT_SCREENS[moduleCode] ?? [];

  return (
    <AppShell titleHe={entitlement.displayNameHe} backToParent={back}>
      <section className="tiles">
        {parents.map((p) => (
          // Nouns. Each one is a parent screen with its own address, not a toolbar button.
          <article key={p.slug} className="tile">
            <h2 className="tile__title">{p.titleHe}</h2>
            <a className="btn btn--primary" href={routes.parentScreen(companyUid, moduleCode, p.slug)}>
              פתיחה
            </a>
          </article>
        ))}
      </section>
    </AppShell>
  );
}
